/**
 * Backend-importable color tokens for seed data.
 *
 * Hex values mirror `baseColors.<name>[500]` (and a few off-step picks) from
 * `23-design-system-ui/src/theme/palette.ts`. Defined here in shared-types so
 * backend services and seed scripts can reference tokens by name instead of
 * pasting raw hex into every row — without forcing a circular dependency
 * (design-system-ui already imports shared-types, not the other way around).
 *
 * If/when the palette moves into shared-types as the source of truth,
 * `baseColors` can re-export from this module and the duplication collapses.
 *
 * Single source of truth for seed-data colors. If you change a value here,
 * also update the matching scale in design-system-ui.
 */
export const SEED_COLORS = {
  // Brand
  primary: '#635BFF', // baseColors.primary[500]
  secondary: '#00D4FF', // baseColors.secondary[500]
  teal: '#0FB5AE', // baseColors.teal[500]
  indigo: '#6366F1', // baseColors.indigo[500]

  // Semantic 500
  blue: '#3B82F6', // baseColors.info[500]
  green: '#10B981', // baseColors.success[500]
  amber: '#F59E0B', // baseColors.warning[500]
  red: '#EF4444', // baseColors.error[500]
  purple: '#8B5CF6', // baseColors.violet[500]

  // Semantic 600 (slightly deeper — used for terminal/elevated states)
  greenDeep: '#059669', // baseColors.success[600]
  orange: '#F97316', // baseColors.warning[600]

  // Neutral
  gray: '#6B7280', // baseColors.neutral[500]
  grayLight: '#9CA3AF', // baseColors.neutral[400]
} as const;

export type SeedColor = (typeof SEED_COLORS)[keyof typeof SEED_COLORS];
